import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { COMMON_TYPE } from '@/types/enums';
import { ConfirmEmailPostRequest } from '@/types/http-payload/auth';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import StepProgressBar from '@/components/general/step-indicator';
import { BB_MAIL, EMAIL_VALIDATE_REGEX } from '@/configs/constants';
import CircleHelpIcon from '@/assets/icons/circle-help-icon.svg';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/common/Popover';
import { useState } from 'react';
import AnimateButton from '@/components/general/button-custom/AnimateButton';
import SuccessStatusImg from '@/assets/icons/success-noti-icon.png';

export default observer(function SignUpPage() {
    // hooks
    const { t } = useTranslation();
    const { pathname } = useLocation();

    // store
    const {
        authStore: { confirmEmailRegister }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .required('form_error_validate.required')
            .matches(EMAIL_VALIDATE_REGEX, 'form_error_validate.email')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // form
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        reValidateMode: 'onSubmit',
        defaultValues: {
            email: ''
        }
    });

    // state
    const [openNote, setOpenNote] = useState<boolean>(false);
    const [isSendMailSuccess, setIsSendMailSuccess] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const dataSubmit: ConfirmEmailPostRequest = {
            email: data.email,
            type: isPathEk(pathname) ? COMMON_TYPE.EK : COMMON_TYPE.GAS
        };
        const res = await flowResult(confirmEmailRegister(dataSubmit));
        if (res) {
            setIsSendMailSuccess(true);
        }
    };

    return (
        <div>
            <h3 className='title-1 text-center mb-3'>
                {t('words_title.register_account')}
            </h3>

            {
                isSendMailSuccess ?
                    (
                        <div className='pt-8 pb-8'>
                            <div className='flex justify-center mb-8'>
                                <img src={SuccessStatusImg} alt='' className='w-14' />
                            </div>
                            <div className='text-center'>
                                ご入力頂いたメールアドレス宛てにメールをお送りいたしました。
                                <br />
                                メール内に記載されたURLをクリックして、本登録を行って下さい。
                            </div>
                        </div>
                    ) :
                    (
                        <>
                            <div className='pt-6'>
                                <StepProgressBar
                                    startingStep={0}
                                    wrapperClass='w-full'
                                    steps={[
                                        {
                                            label: t('words_title.register_page.step_1'),
                                            name: 'register_page.step_1'
                                        },
                                        {
                                            label: t('words_title.register_page.step_2'),
                                            name: 'register_page.step_2'
                                        },
                                        {
                                            label: t('words_title.register_page.step_3'),
                                            name: 'register_page.step_3'
                                        }
                                    ]}
                                />
                            </div>
                            <div className='h-9 w-full'></div>

                            <div className='text-center'>
                                ベストBikeレンタルをご利用いただくには会員登録が必要です。
                                <br />
                                会員登録をするメールアドレスを入力して、送信ボタンをクリックして下さい。
                            </div>

                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className='mt-3'>
                                    <div>
                                        <Popover open={openNote} onOpenChange={setOpenNote}>
                                            <PopoverTrigger asChild>
                                                <div className='inline-flex gap-1.5 items-center cursor-pointer hover:underline hover:decoration-1 mb-4'>
                                                    <span className='text-[#666666]'>For foreign residents</span>
                                                    <img
                                                        src={CircleHelpIcon}
                                                        alt=''
                                                        className='w-[1.125rem] h-[1.125rem]'
                                                    />
                                                </div>
                                            </PopoverTrigger>
                                            <PopoverContent
                                                side='top'
                                                align='start'
                                                className='max-w-[200px] sm:max-w-[400px] w-fit p-2 text-[#fff] bg-[#333437] shadow-[0px_2px_8px_0px_rgba(0,0,0,0.15)]'
                                            >
                                                <div className='text-[1rem]'>
                                                    The reservation system is only in Japanese.
                                                    <br />
                                                    If you have any concerns about using it, please email us.
                                                    <br />
                                                    <a
                                                        href={`mailto:${BB_MAIL}`}
                                                        className='link-normal text-[#009DFF]'
                                                    >
                                                        {BB_MAIL}
                                                    </a>
                                                </div>
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    <FormField
                                        control={form.control}
                                        name='email'
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type='text'
                                                        placeholder={t('field.email')}
                                                        isHalfWidth
                                                        {...field}
                                                        onChange={(e) => {
                                                            field.onChange(e);
                                                            form.clearErrors('email');
                                                        }}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <div className='flex justify-center'>
                                        <AnimateButton
                                            contentComp={t('button.send')}
                                            type='submit'
                                            parentClassName='mt-8 w-[208px] h-[48px]'
                                            disabled={!form.watch('email')}
                                            outline
                                        >
                                        </AnimateButton>
                                    </div>
                                </form>
                            </Form>
                        </>
                    )
            }

        </div>
    );
});
